/**
 * Where your account stands.
 *
 * Moderation used to be invisible to the person it happened to: a warning
 * arrived as a notification you could scroll past, a mute expired in
 * silence, and nobody could answer "am I in trouble" without asking. The
 * first thing most people learned about their own record was the ban.
 *
 * It is one row. The first version stacked a sentence, then a five-step
 * scale on a line of its own, then a list - three bands of nothing much,
 * which is a lot of height to say "you are fine", and that is what it says
 * almost always. The scale now sits on the same line as the sentence and
 * the reasons fold out underneath, so a clean account is one line and a
 * bad one is as tall as it needs to be.
 *
 * The reasons matter as much as the level. "Limited" on its own is a
 * verdict with no cause and no way out, which is the thing that makes
 * people angry rather than careful - so every entry says what happened and
 * the date it stops counting.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

export type Standing = {
  level: number;
  id: string;
  label: string;
  note: string;
  /** The same thing said about somebody else, for a moderator reading it. */
  staffNote?: string;
  steps: string[];
  violations: { kind: string; reason: string; at: number; minutes: number | null; expiresAt: number }[];
  muted: { until: number; reason: string } | null;
  banned: { reason: string; until: number | null } | null;
  decayDays: number;
};

/** The colour of a level. Green only at the top; nothing else earns it. */
function toneOf(level: number): string {
  if (level === 0) return 'var(--good)';
  if (level === 1 || level === 2) return 'var(--warn)';
  return 'var(--bad)';
}

function when(ts: number): string {
  return new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function AccountStanding({ standing, compact }: { standing?: Standing; compact?: boolean }) {
  /* Given one, draw it. Otherwise ask - staff pages pass one in, your own
     settings page has it already from the account. */
  const q = useQuery({
    queryKey: ['me', 'standing'],
    queryFn: () => api.get<{ standing: Standing }>('/api/me/standing'),
    enabled: !standing
  });
  const s = standing ?? q.data?.standing;
  /* Passed one in means you are reading somebody else's - a moderator on a
     report, not the person it happened to. The sentence changes with it. */
  const mine = !standing;

  /* Open when there is something to answer for, shut when there is not -
     nobody opens "nothing on your record" to read the nothing. */
  const [open, setOpen] = useState(false);

  if (!s) {
    return <div className="h-[58px] animate-pulse rounded-[var(--radius-l)] bg-surface-2" />;
  }

  const tone = toneOf(s.level);
  const reasons = s.violations.length + (s.banned ? 1 : 0) + (s.muted ? 1 : 0);

  return (
    <div className="min-w-0">
      {/* ---- the one line ---- */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
        <span
          className="grid h-8 w-8 flex-none place-items-center rounded-full"
          style={{ background: 'color-mix(in srgb, ' + tone + ' 18%, transparent)', color: tone }}
        >
          <Icon name={s.level === 0 ? 'checkCirc' : s.level === 4 ? 'ban' : 'alert'} size={17} />
        </span>

        <span className="min-w-[180px] flex-1 text-[15px] leading-[1.35] font-bold text-ink">
          {mine ? 'Your account is' : 'This account is'}{' '}
          <span style={{ color: tone }}>{s.label.toLowerCase()}</span>
        </span>

        {/*
          The scale, beside the sentence rather than under it.

          Five dots and the one you are on, no labels: the words are the
          sentence to its left, and printing "All good / Limited / Very
          limited / At risk / Suspended" underneath as well was saying the
          same thing twice in twice the height. Hovering names them.
        */}
        <span className="flex flex-none items-center gap-1" role="img" aria-label={'Step ' + (s.level + 1) + ' of 5: ' + s.label}>
          {s.steps.map((label, i) => {
            const here = i === s.level;
            const behind = i < s.level;
            return (
              <span
                key={label}
                title={label}
                className={cn(
                  'rounded-full transition-[width,height,background-color] duration-300 ease-[var(--ease-out)] motion-reduce:transition-none',
                  here ? 'h-[9px] w-[22px]' : 'h-[7px] w-[7px]'
                )}
                style={{
                  background: behind || here ? tone : 'var(--bg-3)',
                  boxShadow: here ? '0 0 0 3px color-mix(in srgb, ' + tone + ' 20%, transparent)' : 'none'
                }}
              />
            );
          })}
        </span>

        {/* Why, on the same line, and only when there is a why. */}
        {reasons ? (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="flex flex-none items-center gap-1 rounded-[calc(8px*var(--radius-mul,1))] px-2 py-1 text-[12.5px] font-semibold text-mute outline-none transition-[background-color,color] duration-[var(--t-hover)] ease-[var(--ease)] hover:bg-surface-2 hover:text-ink focus-visible:shadow-[0_0_0_2px_var(--accent)]"
          >
            {open ? 'Hide' : 'Why'}
            <Icon
              name="chevronD"
              size={13}
              className={cn('transition-transform duration-[260ms] ease-[var(--ease-out)] motion-reduce:transition-none', open && 'rotate-180')}
            />
          </button>
        ) : (
          <span className="flex-none text-[12px] text-ghost">Nothing on record</span>
        )}
      </div>

      {/*
        The reasons, folded.

        A 0fr/1fr grid row, so opening is a movement to a height nobody has
        measured rather than a jump. Inert while shut, or tabbing lands you
        in a list that is not on screen.
      */}
      {reasons ? (
        <div
          className="grid transition-[grid-template-rows,opacity] duration-[260ms] ease-[var(--ease-out)] motion-reduce:transition-none"
          style={{ gridTemplateRows: open ? '1fr' : '0fr', opacity: open ? 1 : 0 }}
          inert={!open}
        >
          <div className="overflow-hidden">
            <ul className="m-0 mt-3 flex list-none flex-col gap-1.5 p-0">
              {s.banned ? (
                <li className="flex flex-wrap items-baseline gap-x-2 rounded-[var(--radius-s)] bg-[color-mix(in_srgb,var(--bad)_12%,transparent)] px-3 py-2 text-[13px]">
                  <span className="font-semibold text-bad">Suspended</span>
                  <span className="min-w-0 flex-1 text-dim">{s.banned.reason || 'No reason was recorded.'}</span>
                  <span className="text-[11.5px] text-ghost">
                    {s.banned.until ? 'until ' + when(s.banned.until) : 'permanent'}
                  </span>
                </li>
              ) : null}

              {s.muted ? (
                <li className="flex flex-wrap items-baseline gap-x-2 rounded-[var(--radius-s)] bg-[color-mix(in_srgb,var(--warn)_12%,transparent)] px-3 py-2 text-[13px]">
                  <span className="font-semibold text-warn">{mine ? 'You cannot post' : 'Cannot post'}</span>
                  <span className="min-w-0 flex-1 text-dim">{s.muted.reason || 'Against the rules'}</span>
                  <span className="text-[11.5px] text-ghost">until {new Date(s.muted.until).toLocaleString()}</span>
                </li>
              ) : null}

              {s.violations.map((v, i) => (
                <li key={i} className="flex flex-wrap items-baseline gap-x-2 rounded-[var(--radius-s)] bg-surface-2 px-3 py-2 text-[13px]">
                  <span className="font-semibold text-ink">
                    {v.kind === 'warn'
                      ? 'Warning'
                      : 'Muted' + (v.minutes ? ' for ' + (v.minutes >= 60 ? Math.round(v.minutes / 60) + 'h' : v.minutes + 'm') : '')}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-dim">{v.reason}</span>
                  <span className="text-[11.5px] text-ghost">
                    {when(v.at)} · stops counting {when(v.expiresAt)}
                  </span>
                </li>
              ))}
            </ul>

            <p className="m-0 mt-2 text-[12px] text-ghost">
              Anything on {mine ? 'your' : 'this'} record stops counting after {s.decayDays} days.
            </p>
          </div>
        </div>
      ) : null}

      {/* Only the staff sheet passes compact; it has its own heading above. */}
      {compact ? null : null}
    </div>
  );
}
